import classNames from 'classnames';
import { map } from 'lodash';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';

import { Medium, Small } from 'components/MediaQuery';
import { COLLECTION_N_COLUMNS, COLLECTION_ITEM_TYPES } from 'constants/collection';
import { medium, mediumUp, largeUp } from 'constants/mediaQueries';
import CollectionChildContainer from 'containers/CollectionChildContainer';
import CollectionParentGridSizeSelector from 'containers/CollectionParentGridSizeSelector/index';
import CollectionParentStoryCardV2Container from 'containers/CollectionParentStoryCardV2Container';
import CustomCollectionStoryCard, {
  ORIENTATION,
} from 'containers/CustomCollectionStoryCardContainer';
import ParentCollectionBannerContainer from 'containers/ParentCollectionBannerContainer';
import Theme from 'styles/theme';

const Grid = ({ nColumns, rows, banner, hasMobileQuickAddRow }) => {
  return (
    <div className={classNames(`Grid Grid--${nColumns}-column`)}>
      {map(rows, (row, i) => {
        return (
          <Fragment key={`row-${i}`}>
            <div className="Grid__row">
              {map(row, (item, j) => (
                <div
                  className={classNames('Grid__item', `Grid__item--${item.type}`)}
                  key={`item-${item.id || item.type}-${j}`}
                >
                  {item.type === COLLECTION_ITEM_TYPES.CHILD_COLLECTION_PLACEHOLDER && (
                    <CollectionChildContainer isLoading />
                  )}
                  {item.type === COLLECTION_ITEM_TYPES.CHILD_COLLECTION && (
                    <CollectionChildContainer
                      handle={item.id}
                      initialColorwayHandle={item.initialColorwayHandle}
                      hasMobileQuickAddRow={hasMobileQuickAddRow}
                      {...item}
                    />
                  )}
                  {item.type === COLLECTION_ITEM_TYPES.STORY_CARD && (
                    <CustomCollectionStoryCard
                      storyId={item.id}
                      orientation={ORIENTATION.PORTRAIT}
                    />
                  )}

                  {item.type === COLLECTION_ITEM_TYPES.STORY_CARD_V2 && (
                    <CollectionParentStoryCardV2Container storyId={item.id} />
                  )}
                </div>
              ))}
            </div>

            <Small>
              <div className="Grid__size-selector-row" key={`size-selector-row-${i}`}>
                {map(row, (item, j) => (
                  <div
                    className={classNames('Grid__item', `Grid__item--${item.type}`)}
                    key={`size-selector-item--${j}`}
                  >
                    {hasMobileQuickAddRow &&
                      item.type === COLLECTION_ITEM_TYPES.CHILD_COLLECTION && (
                        <CollectionParentGridSizeSelector masterHandle={item.id} {...item} />
                      )}
                  </div>
                ))}
              </div>
            </Small>

            <Medium>
              <div className="Grid__size-selector-row" key={`size-selector-row-${i}`}>
                {map(row, (item, j) => (
                  <div
                    className={classNames('Grid__item', `Grid__item--${item.type}`)}
                    key={`size-selector-item--${j}`}
                  >
                    {hasMobileQuickAddRow &&
                      item.type === COLLECTION_ITEM_TYPES.CHILD_COLLECTION && (
                        <CollectionParentGridSizeSelector masterHandle={item.id} {...item} />
                      )}
                  </div>
                ))}
              </div>
            </Medium>

            {i === 0 && banner && (
              <div className={classNames('Grid__row', 'Grid__row--banner')} key="banner">
                <ParentCollectionBannerContainer
                  className={classNames(
                    'Grid__item',
                    'Grid__item--banner',
                    'ParentCollectionBanner--second'
                  )}
                  key="ColectionBanner"
                  banner={banner}
                />
              </div>
            )}
          </Fragment>
        );
      })}
      <style jsx>
        {`
          .Grid {
            background-color: ${Theme.get('colors-black-10')};
            margin-top: ${Theme.get('spacing') * 4}px;
            margin-bottom: ${Theme.get('spacing') * 4}px;
            width: 100%;
            min-height: 40vh;
          }

          .Grid__row {
            display: flex;
            width: 100%;
            column-gap: ${Theme.get('spacing')}px;
          }

          .Grid__size-selector-row {
            display: flex;
            width: 100%;
            margin-bottom: ${Theme.get('spacing') * 2}px;
            column-gap: ${Theme.get('spacing')}px;
          }

          .Grid__row--banner {
            margin-bottom: ${Theme.get('spacing') * 2}px;
          }

          .Grid__item {
            display: inline-block;
            max-width: 100%;
            padding: 0 ${Theme.get('spacing') * 4}px 0 0;
            flex-shrink: 0;
            width: 100%;
            padding-right: ${Theme.get('spacing') * 5}px;
            padding-left: ${Theme.get('spacing') * 5}px;
          }

          .Grid--${COLLECTION_N_COLUMNS.TWO}-column .Grid__item {
            width: calc(50% - ${Theme.get('spacing') / 2}px);
            padding: 0;
          }

          .Grid--${COLLECTION_N_COLUMNS.TWO}-column
            .Grid__item.Grid__item--${COLLECTION_ITEM_TYPES.STORY_CARD_V2} {
            width: 100%;
            padding: 0;
          }

          @media ${medium} {
            .Grid__size-selector-row {
              column-gap: ${Theme.get('spacing') * 2}px;
            }

            .Grid--${COLLECTION_N_COLUMNS.TWO}-column .Grid__row {
              margin-bottom: 0;
            }
          }

          @media ${mediumUp} {
            .Grid__row {
              margin-bottom: ${Theme.get('spacing') * 9}px;
              column-gap: ${Theme.get('spacing') * 2}px;
            }

            .Grid--${COLLECTION_N_COLUMNS.ONE}-column .Grid__item {
              width: 100%;
            }

            .Grid--${COLLECTION_N_COLUMNS.TWO}-column .Grid__item {
              width: calc(50% - ${Theme.get('spacing-px')});
            }

            .Grid--${COLLECTION_N_COLUMNS.THREE}-column .Grid__item {
              width: 33.3%;
            }

            .Grid__item {
              padding: 0 ${Theme.get('spacing') * 4}px 0 0;
            }
          }

          @media ${largeUp} {
            .Grid {
              background-color: ${Theme.get('colors-white')};
            }

            .Grid--${COLLECTION_N_COLUMNS.TWO}-column
              .Grid__item.Grid__item--${COLLECTION_ITEM_TYPES.STORY_CARD_V2} {
              padding: 0 ${Theme.get('spacing') * 4}px;
            }

            .Grid__row {
              column-gap: 0px;
            }
          }
        `}
      </style>

      <style jsx global>
        {`
          .Grid__size-selector-row .MobileSizeSelector .MobileSizeSelector__grid-item {
            width: ${100 / 4}%;
          }

          .Grid__size-selector-row .MobileSizeSelector .MobileSizeSelector__grid-item--3-column {
            width: 50%;
            flex-grow: 1;
          }

          @media ${medium} {
            .Grid--${COLLECTION_N_COLUMNS.TWO}-column .Grid__item .CollectionChild {
              height: 100%;
            }
          }
        `}
      </style>
    </div>
  );
};

Grid.propTypes = {
  banner: PropTypes.shape({}),
  rows: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf(Object.values(COLLECTION_ITEM_TYPES)),
        id: PropTypes.string,
      })
    )
  ).isRequired,
  nColumns: PropTypes.number.isRequired,
  hasMobileQuickAddRow: PropTypes.bool,
};

Grid.defaultProps = {
  banner: null,
};

export default Grid;
